import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Paper,
  Chip,
  Collapse
} from '@mui/material';
import { Close as CloseIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
import { UserLog } from '../../services/admin.service';
import { useState } from 'react';

interface UserLogsModalProps {
  open: boolean;
  onClose: () => void;
  userName: string;
  userLogs: UserLog[];
  loading: boolean;
  error: string | null;
}

const UserLogsModal: React.FC<UserLogsModalProps> = ({
  open,
  onClose,
  userName,
  userLogs,
  loading,
  error
}) => {
  const [expandedLogs, setExpandedLogs] = useState<Record<string, boolean>>({});
  
  const toggleDetails = (logId: string) => {
    setExpandedLogs(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };
  
  const formatDetails = (details: string | null) => {
    if (!details) return null;
    
    try {
      const parsedDetails = JSON.parse(details);
      return (
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
          {JSON.stringify(parsedDetails, null, 2)}
        </pre>
      );
    } catch (e) {
      return details;
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="logs-dialog-title"
    >
      <DialogTitle id="logs-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          Activity Logs - {userName}
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : userLogs.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="body1" color="text.secondary">
              No activity logs found for this user.
            </Typography>
          </Box>
        ) : (
          <Paper elevation={0} variant="outlined" sx={{ mb: 2 }}>
            <TableContainer>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userLogs.map((log) => (
                    <>
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Chip 
                            label={log.action} 
                            size="small" 
                            color={getActionColor(log.action)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                        <TableCell>
                          {log.details ? (
                            <Button 
                              size="small" 
                              variant="text" 
                              onClick={() => toggleDetails(log.id)}
                              endIcon={expandedLogs[log.id] ? <ExpandLess /> : <ExpandMore />}
                            >
                              {expandedLogs[log.id] ? 'Hide Details' : 'View Details'}
                            </Button>
                          ) : 'N/A'}
                        </TableCell>
                      </TableRow>
                      {log.details && (
                        <TableRow>
                          <TableCell colSpan={3} style={{ padding: 0 }}>
                            <Collapse in={expandedLogs[log.id]} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, backgroundColor: '#2b2b2b', color: '#fff', borderRadius: '4px' }}>
                                {formatDetails(log.details)}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Helper function to determine chip color based on action type
const getActionColor = (action: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const actionLower = action.toLowerCase();
  
  if (actionLower.includes('tax')) return 'error';
  if (actionLower.includes('transaction')) return 'success';
  if (actionLower.includes('log in') || actionLower.includes('login') || actionLower.includes('logout') || actionLower.includes('log out')) return 'primary';
  if (actionLower.includes('integration') || actionLower.includes('import')) return 'secondary';
  if (actionLower.includes('update')) return 'info';
  if (actionLower.includes('view')) return 'default';
  
  return 'default';
};

export default UserLogsModal;
